import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { PageContext } from '../../modules/context/PageContext';

import { useProfileStore } from '../../modules/store/profileStore';
import { SavedCard, useSavedCardsStore } from '../../modules/store/savedCardsStore';
import { getCustomClickTrackValues, mapTrackingProperties } from '../../modules';
import { useWalletWidgetBadgePublisher, WalletSectionUxlError } from '../WalletWidgetContainer';
import {
  WALLET_SAVED_CARDS_TEST_IDS,
  SAVED_CARDS_MODAL_IDS,
  MAX_SAVED_CARDS,
} from './WalletSavedCardsSection.constants';
import { useWalletSavedCards } from './hooks/useWalletSavedCards';
import { SavedCardRow } from './molecules/SavedCardRow';
import { AddCardRow } from './molecules/AddCardRow';
import { BoundlessBenefitsBanner } from './molecules/BoundlessBenefitsBanner/BoundlessBenefitsBanner';
import { StyledWalletSavedCardsSection } from './WalletSavedCardsSection.styles';
import { WalletSavedCardsSectionProps } from './WalletSavedCardsSection.types';
import { walletSavedCardsSectionMock } from './__mock__/WalletSavedCardsSectionMock';
import { hasBoundlessSavedCard } from './utils/hasBoundlessSavedCard';

/**
 * WalletSavedCardsSection - Orchestrator component for saved cards in Wallet Tab
 * Reuses Gift Cards section styling and structure patterns
 */
export const WalletSavedCardsSection: FC<WalletSavedCardsSectionProps> = ({
  isAuthorMode,
  model,
  pageContext: pageContextProp,
}) => {
  const { IS_LOCAL_DEV } = process.env;
  const reactPageContext = React.useContext(PageContext);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageContext = (pageContextProp ?? reactPageContext) as Record<string, any>;

  const isLocalDev = IS_LOCAL_DEV === 'true';
  const useMockData = Boolean(isAuthorMode) || isLocalDev;

  // Store access
  const { setProfileModalId, setPaymentId } = useProfileStore(state => state);
  const { savedCards } = useSavedCardsStore(state => ({
    savedCards: state.savedCards,
  }));

  // Hide guard
  const isHidden = Boolean(model?.hideComponent) && !useMockData;

  // Seed mock data for author mode and local dev
  useEffect(() => {
    if (useMockData && !isHidden) {
      useSavedCardsStore.getState().setProfileSavedCards(walletSavedCardsSectionMock);
    }
  }, [useMockData, isHidden]);

  // Fetch saved cards from API
  const { hasUxlError } = useWalletSavedCards({
    pageContext,
    isLocal: isLocalDev,
    skip: useMockData,
  });

  // Use mock data if in mock mode, otherwise use real data
  const cardsToDisplay = useMemo(() => {
    if (useMockData) {
      return walletSavedCardsSectionMock;
    }
    // In production, use data from store (populated by API via useWalletSavedCards)
    return savedCards || [];
  }, [useMockData, savedCards]);

  // Loading state
  const isLoading = useMemo(() => {
    // Always false for now - we're always showing mock data
    return false;
  }, []);

  // Sort cards: Primary first, then by order. Profile renders at most MAX_SAVED_CARDS rows.
  const sortedCards = useMemo(() => {
    if (!cardsToDisplay || cardsToDisplay.length === 0) return [];
    const sorted = [...cardsToDisplay];
    sorted.sort((a, b) => {
      if (a.isPrimary) return -1;
      if (b.isPrimary) return 1;
      return 0;
    });
    return sorted.slice(0, MAX_SAVED_CARDS);
  }, [cardsToDisplay]);

  const cardCount = cardsToDisplay?.length ?? 0;
  const hasCards = !isLoading && sortedCards.length > 0;
  const isAtMaxCards = cardCount >= MAX_SAVED_CARDS;
  const memberHasBoundlessCard = useMemo(() => hasBoundlessSavedCard(cardsToDisplay), [cardsToDisplay]);
  const showBoundlessBanner = Boolean(model?.boundless && memberHasBoundlessCard);

  // Badge text (singular/plural handling)
  const badgeText = useMemo(() => {
    if (isHidden) return undefined;
    if (cardCount === 0) {
      return model?.defaultLabel;
    }
    // Use singular label for count === 1, plural otherwise
    const label = cardCount === 1 ? model?.availableSingularLabel : model?.availablePluralLabel;
    if (label?.includes('{0}')) {
      return label.replace('{0}', String(cardCount));
    }
    return label ? `${cardCount} ${label}` : undefined;
  }, [cardCount, isHidden, model?.defaultLabel, model?.availableSingularLabel, model?.availablePluralLabel]);

  useWalletWidgetBadgePublisher(badgeText);

  // Extract labels from nested structure
  const addCardLabels = {
    title: model?.addCard?.sectionHeading,
    subtitle: model?.addCard?.description,
    ctaLabel: model?.addCard?.ctaLinkLabel,
  };

  const cardListLabels = {
    primaryLabel: model?.cardList?.primaryLabel,
    editCtaLabel: model?.cardList?.editLinkLabel,
  };

  const addCtaTracking = useMemo(() => {
    const trackingProperties = mapTrackingProperties(model?.addCard?.tracking);
    return {
      trackingProperties,
      customClickTrackValue: getCustomClickTrackValues(trackingProperties, 'internal'),
    };
  }, [model?.addCard?.tracking]);

  const editCtaTracking = useMemo(() => {
    const trackingProperties = mapTrackingProperties(model?.cardList?.tracking);
    return {
      trackingProperties,
      customClickTrackValue: getCustomClickTrackValues(trackingProperties, 'internal'),
    };
  }, [model?.cardList?.tracking]);

  const boundlessCtaTracking = useMemo(
    () => mapTrackingProperties(showBoundlessBanner ? model?.boundless?.tracking : undefined),
    [model?.boundless?.tracking, showBoundlessBanner]
  );
  const boundlessCtaCustomTrack = useMemo(
    () => getCustomClickTrackValues(boundlessCtaTracking),
    [boundlessCtaTracking]
  );

  // Modal handlers — mirror ProfileSection so the BAU AddCard/UpdateCard modals open unchanged.
  // AddCard resolves the card being edited from useProfileStore.paymentId, not the saved cards store.
  const handleAddCardClick = useCallback(() => {
    setPaymentId('');
    setProfileModalId(SAVED_CARDS_MODAL_IDS.ADD_CARD);
  }, [setPaymentId, setProfileModalId]);

  const handleEditCardClick = useCallback(
    (card: SavedCard) => {
      setProfileModalId(SAVED_CARDS_MODAL_IDS.EDIT_CARD);
      setPaymentId(card.id);
    },
    [setPaymentId, setProfileModalId]
  );

  const _handleDeleteCardClick = useCallback(
    (card: SavedCard) => {
      useSavedCardsStore.getState().setSelectedSavedCard(card);
      setProfileModalId(SAVED_CARDS_MODAL_IDS.DELETE_CARD);
    },
    [setProfileModalId]
  );

  const _handleFolioDeliveryClick = useCallback(
    (card: SavedCard) => {
      useSavedCardsStore.getState().setSelectedSavedCard(card);
      setProfileModalId(SAVED_CARDS_MODAL_IDS.FOLIO_DELIVERY);
    },
    [setProfileModalId]
  );

  // Early return after all hooks
  if (isHidden) {
    return null;
  }

  return (
    <StyledWalletSavedCardsSection
      data-testid={WALLET_SAVED_CARDS_TEST_IDS.root}
      data-component-name="o-account-WalletSavedCardsSection"
      id={model?.componentId}
      className={model?.customClass}
    >
      {hasUxlError ? <WalletSectionUxlError /> : null}

      {showBoundlessBanner && model?.boundless ? (
        <BoundlessBenefitsBanner
          boundless={model.boundless}
          trackingProperties={boundlessCtaTracking}
          customClickTrackValue={boundlessCtaCustomTrack}
        />
      ) : null}

      {/* Cards list */}
      {!hasUxlError && hasCards && (
        <div className="wallet-saved-cards-section__cards-list" data-testid={WALLET_SAVED_CARDS_TEST_IDS.cardsList}>
          {sortedCards.map(card => (
            <SavedCardRow
              key={card.id}
              card={card}
              primaryLabel={cardListLabels.primaryLabel}
              editCtaLabel={cardListLabels.editCtaLabel}
              editCtaAriaLabel={cardListLabels.editCtaLabel}
              onEditClick={handleEditCardClick}
              testId={WALLET_SAVED_CARDS_TEST_IDS.cardRow}
              trackingProperties={editCtaTracking.trackingProperties}
              customClickTrackValue={editCtaTracking.customClickTrackValue}
            />
          ))}
        </div>
      )}

      {/* Add Card row - hidden once the member reaches the Profile card limit */}
      {!hasUxlError && !isAtMaxCards && (
        <AddCardRow
          title={addCardLabels.title}
          subtitle={addCardLabels.subtitle}
          ctaLabel={addCardLabels.ctaLabel}
          ctaAriaLabel={addCardLabels.ctaLabel}
          onAddClick={handleAddCardClick}
          testId={WALLET_SAVED_CARDS_TEST_IDS.addCardRow}
          showDivider={hasCards}
          trackingProperties={addCtaTracking.trackingProperties}
          customClickTrackValue={addCtaTracking.customClickTrackValue}
        />
      )}
    </StyledWalletSavedCardsSection>
  );
};
